<template>
    <v-card v-if="loading" flat color="white" class="d-flex flex-column fill-height justify-center pa-8" elevation="4">
        <v-progress-linear indeterminate color="primary" height="4"></v-progress-linear>
    </v-card>

    <v-card v-else-if="item && agency" flat color="white" class="scrollbox2 fill-height pa-4" elevation="4">
        <v-toolbar dense color="#2B3848" dark>
            <v-icon class="mr-4">mdi-text-box-outline</v-icon>
            <v-toolbar-title>Reserva {{ item.reference }} para el {{ formattedDate }}</v-toolbar-title>
            <v-spacer />
            <v-btn dark color="primary" @click="openBooking">Abrir reserva</v-btn>
        </v-toolbar>

        <v-list>
            <v-list-item>
                <v-list-item-icon>
                    <img v-if="agency.imageId" :src="`/uploads/${agency.imageId}/128`" width="24" height="24" class="mt-2" />
                    <v-icon v-else>mdi-calendar</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-caption font-weight-bold">Agencia</v-list-item-title>
                    <v-list-item-title>
                        {{ item.agencyName }}
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-list-item>
                <v-list-item-icon>
                    <v-icon>mdi-account-multiple</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-caption font-weight-bold">Clientes e idioma</v-list-item-title>
                    <v-list-item-title class="d-flex align-center">
                        <span v-if="item.adults" class="mr-2">{{ item.adults }} adultos</span>
                        <span v-if="item.children" class="mr-2">{{ item.children }} niños</span>
                        <span v-if="item.babies" class="mr-2">{{ item.babies }} bebés</span>
                        <LanguageIcon :language="item.language" class="ml-2" />
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-list-item>
                <v-list-item-icon>
                    <v-icon>mdi-sitemap-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-caption font-weight-bold">Producto</v-list-item-title>
                    <v-list-item-title>
                        {{ item.productName }}
                        <span class="ml-2">
                            (
                            <strong>{{ item.productCode }}</strong>
                            )
                        </span>
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-list-item>
                <v-list-item-icon>
                    <v-icon>mdi-bed-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-caption font-weight-bold">Hotel</v-list-item-title>
                    <v-list-item-title>{{ item.hotelName }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.hotelRoom">Habitación {{ item.hotelRoom }}</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>

            <v-list-item>
                <v-list-item-icon>
                    <v-icon>mdi-clock-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-caption font-weight-bold">Recogida</v-list-item-title>
                    <v-list-item-title>{{ item.pickupTime }} en {{ item.meetingPointName }}</v-list-item-title>
                </v-list-item-content>
            </v-list-item>

            <v-list-item>
                <v-list-item-icon>
                    <v-icon>mdi-account</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-caption font-weight-bold">Cliente</v-list-item-title>
                    <v-list-item-title>{{ item.customerName || "Nombre del cliente no especificado" }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="item.customerEmail">
                    <span class="text-caption blue darken-2 white--text rounded px-2 py-1 font-weight-medium ml-2">
                        <v-icon small color="white" class="mr-1">mdi-email</v-icon>
                        {{ item.customerEmail }}
                    </span>
                </v-list-item-action>
                <v-list-item-action v-if="item.customerPhone">
                    <span class="text-caption blue darken-2 white--text rounded px-2 py-1 font-weight-medium ml-2">
                        <v-icon small color="white" class="mr-1">mdi-phone</v-icon>
                        {{ item.customerPhone }}
                    </span>
                </v-list-item-action>
            </v-list-item>

            <v-list-item v-if="item.comments">
                <v-list-item-icon>
                    <v-icon>mdi-comment-multiple-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                    <v-list-item-title class="text-caption font-weight-bold">Comentarios</v-list-item-title>
                    <v-list-item-subtitle class="text-wrap" style="white-space: pre-wrap">{{ item.comments }}</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </v-list>

        <template v-if="routes.length > 0">
            <v-toolbar dense color="#2B3848" dark class="mt-4">
                <v-icon class="mr-4">mdi-home-clock-outline</v-icon>
                <v-toolbar-title>Operativa</v-toolbar-title>
                <v-spacer />
            </v-toolbar>
            <div v-for="zone in routes" :key="zone.zoneId">
                <div v-for="(service, i) in zone.services" :key="`${zone.zoneId}-${i}`" class="d-flex flex-column pa-2 data-row text-caption">
                    <div class="blue darken-2 white--text d-flex align-center pa-1 text-body-1 font-weight-bold">
                        <span>
                            <span v-if="service.status === 'PENDING'" class="grey darken-2 white--text rounded px-1">PENDIENTE</span>
                            <span v-else-if="service.status === 'PICKUP'" class="orange darken-2 white--text rounded px-1">EN RECOGIDA</span>
                            <span v-else-if="service.status === 'TOUR'" class="green white--text rounded px-1">TOUR</span>
                            <span v-else-if="service.status === 'RETURN'" class="orange darken--2 white--text rounded px-1">RETORNO</span>
                            <span v-else-if="service.status === 'CLOSED'" class="red darken--2 white--text rounded px-1">FINALIZADA</span>
                            <span v-if="service.busNumber" class="ml-4">Bus {{ service.busNumber }}</span>
                        </span>
                        <span class="ml-2 font-weight-bold">{{ service.name }}</span>
                        <span class="ml-2">{{ service.guideName }}</span>
                    </div>

                    <div class="data-column d-flex scrollbox2-horizontal mt-2" style="width: 100%">
                        <template v-for="(meetingPoint, j) in service.meetingPoints">
                            <div :key="`mp-data-${i}-${j}`" class="d-flex flex-column align-center text-caption" :style="getMeetingPointStyle(meetingPoint)">
                                <!-- <span> -->
                                <span v-if="meetingPoint.checked" class="rounded px-1 text-body-2 font-weight-bold text-center" :class="getMeetingPointColor(meetingPoint)" style="width: 100%">
                                    {{ meetingPoint.time }}
                                    <span class="ml-1 font-weight-light">{{ getMeetingPointLastPickupTime(meetingPoint) }}</span>
                                </span>
                                <span v-else class="grey white--text rounded px-1 text-body-2 text-center" style="width: 100%">{{ meetingPoint.time }}</span>
                                <!-- </span> -->
                                <span class="atext-truncate text-center" style="min-width: 96px; max-width: 96px">{{ meetingPoint.name }}</span>
                            </div>

                            <v-divider inline v-if="j < service.meetingPoints.length - 1" :key="`mp-divider-${i}-${j}`" style="min-width: 16px; max-width: 32px; margin-top: 8px"></v-divider>
                        </template>
                    </div>
                </div>
            </div>
        </template>

        <template v-if="conversationId">
            <v-toolbar dense color="#2B3848" dark class="mt-4">
                <v-icon class="mr-4">mdi-chat</v-icon>
                <v-toolbar-title>Conversación</v-toolbar-title>
                <v-spacer />
                <v-btn dark color="primary" @click="openCrm">CRM</v-btn>
            </v-toolbar>
            <ConversationView v-if="conversationId" :conversationId="conversationId" style="height: auto" />
        </template>
    </v-card>
</template>

<style lang="scss"></style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BaseView } from "@/components/BaseView";
import { DataClient } from "@/services/DataClient";
import { Utilities } from "@/services/Utilities";
import { GetBookingResponse, GetAgencyResponse, GetDayScheduleBusRoutesResponse } from "@/services/ClientInterfaces";
import ConversationView from "@/views/Crm/ConversationView.vue";

@Component({
    components: {
        ConversationView,
    },
})
export default class extends BaseView {
    @Prop({ required: true, type: String })
    itemId!: string;

    loading = true;
    item: GetBookingResponse | null = null;
    agency: GetAgencyResponse | null = null;
    routes: GetDayScheduleBusRoutesResponse = [];
    conversationId = "";

    @Watch("itemId", { immediate: true })
    async refresh() {
        this.conversationId = "";
        this.loading = true;

        this.item = await DataClient.Bookings.get({ id: this.itemId });
        this.agency = await DataClient.Agencies.get({ id: this.item.agencyId });

        const routes = await DataClient.DaySchedules.getBusRoutes({ date: this.item.date });
        const pickupInformation = await DataClient.Bookings.getPickupInformation({ id: this.itemId });

        this.routes = [];

        for (const zone of routes) {
            const services: GetDayScheduleBusRoutesResponse[number]["services"] = [];

            for (const service of zone.services) {
                let addService = service.id === pickupInformation.pickupServiceId || service.id === pickupInformation.activityServiceId;
                if (addService) {
                    services.push(service);
                }
            }

            if (services.length) {
                this.routes.push({
                    ...zone,
                    services,
                });
            }
        }

        this.conversationId = this.item.conversationId;
        this.loading = false;
    }

    get formattedDate() {
        const date = this.item?.date || "";
        return Utilities.getDayOfWeekNameYYYYMMDD(date).toLowerCase() + ", " + Utilities.formatDateToDDMMYYYY(date);
    }

    openBooking() {
        this.$emit("open", this.item?.id);
    }

    async openCrm() {
        await DataClient.Crm.open({ bookingId: this.item?.id });
    }

    getMeetingPointLastPickupTime(meetingPoint: GetDayScheduleBusRoutesResponse[number]["services"][number]["meetingPoints"][number]) {
        const lastPickupTime = meetingPoint.lastPickupTime;

        if (lastPickupTime) {
            // extract the time (HH:MM) from a the string like " 2023-08-04T11:48:35.433Z"
            return lastPickupTime.substring(11, 16);
        }

        return "";
    }

    getMeetingPointColor(meetingPoint: GetDayScheduleBusRoutesResponse[number]["services"][number]["meetingPoints"][number]) {
        const lastPickupTime = this.getMeetingPointLastPickupTime(meetingPoint);

        if (lastPickupTime) {
            if (lastPickupTime > meetingPoint.time) {
                return "red darken-1 white--text";
            }
        }

        return "green white--text";
    }

    getMeetingPointStyle(meetingPoint: GetDayScheduleBusRoutesResponse[number]["services"][number]["meetingPoints"][number]) {
        if (meetingPoint.id === this.item?.meetingPointId) {
            return "border: 3px dotted #2196f3; border-radius: 4px; padding: 4px;";
        }

        return "border: 3px; border-radius: 4px; padding: 4px;";
    }
}
</script>
